import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { utils } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import optionTypes from '../constants/option-types';
import ElementSettingsContext from '../helpers/element-settings-context';
import getOptionComputedAttributesValues from '../helpers/get-option-computed-attributes-values';

const { SliderPattern } = designSystem;

const getPopoverProps = (prop) => {
  if (!prop) return {};

  return {
    ...prop,
    content: prop.renderContent?.() ?? prop.content ?? '',
  };
};

const isIndeterminate = (attributes) =>
  attributes.some((value, index, values) => value !== values[0]);

function SliderOption({ option, settingId, onUpdateValue }) {
  const { state, callbacks } = useContext(ElementSettingsContext);
  const attributesValues = useMemo(
    () => getOptionComputedAttributesValues(state, option),
    [option, state]
  );
  const flattenedAttributesValues = attributesValues.flat();
  const indeterminate = isIndeterminate(flattenedAttributesValues);
  const currentValue = Number(
    indeterminate ? Math.max(...flattenedAttributesValues) : flattenedAttributesValues[0]
  );
  const value = currentValue || option.min;
  const isMultipleElements = option.elementIds.length > 1;

  const popoverProps = useMemo(() => getPopoverProps(option.popover), [option.popover]);
  const valuePopoverProps = useMemo(
    () => getPopoverProps(option.valuePopover),
    [option.valuePopover]
  );

  const createSliderChangeHandler = useCallback(
    (attributes) => (newValue) => {
      if (!utils.isNumeric(Number(newValue))) {
        return;
      }

      onUpdateValue(`${newValue}`, attributes);
    },
    [onUpdateValue]
  );

  const createSliderAfterChangeHandler = useCallback(
    (optionId) => (newValue) => {
      if (!utils.isNumeric(Number(newValue))) {
        return;
      }

      callbacks.onOptionClicked({ optionId, settingId });
    },
    [callbacks, settingId]
  );

  const handleChange = useMemo(
    () => createSliderChangeHandler(option.attributes),
    [createSliderChangeHandler, option.attributes]
  );

  const handleAfterChange = useMemo(
    () => createSliderAfterChangeHandler(option.id),
    [createSliderAfterChangeHandler, option.id]
  );

  const items = useMemo(
    () =>
      option.options?.map((opt, index) => {
        const subAttributesValues = [attributesValues[index]].flat();
        const subIndeterminate = isMultipleElements && isIndeterminate(subAttributesValues);
        const subCurrentValue = Number(
          subIndeterminate ? Math.max(...subAttributesValues) : subAttributesValues[0]
        );
        const subValue = subCurrentValue || option.min;

        return {
          value: subValue,
          iconGlyph: opt.iconGlyph,
          popover: getPopoverProps(opt.popover),
          indeterminate: subIndeterminate,
          ...(opt.valuePopover &&
            !subIndeterminate && {
              valuePopoverProps: getPopoverProps(opt.valuePopover),
            }),
          onChange: createSliderChangeHandler(opt.attributes),
          onAfterChange: createSliderAfterChangeHandler(opt.id),
        };
      }) || null,
    [
      attributesValues,
      createSliderAfterChangeHandler,
      createSliderChangeHandler,
      isMultipleElements,
      option.min,
      option.options,
    ]
  );

  return (
    <SliderPattern
      value={value}
      title={option.title}
      items={items}
      iconGlyph={option.iconGlyph}
      mixedLabel={option.mixedLabel}
      step={option.step}
      min={option.min}
      max={option.max}
      indeterminate={indeterminate}
      popoverProps={popoverProps}
      {...(option.valuePopover && !indeterminate && { valuePopoverProps })}
      onChange={handleChange}
      onAfterChange={handleAfterChange}
    />
  );
}

SliderOption.propTypes = {
  option: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: optionTypes.SLIDER.isRequired,
    title: PropTypes.string,
    attributes: PropTypes.arrayOf(PropTypes.string),
    elementIds: PropTypes.arrayOf(PropTypes.string),
    options: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        iconGlyph: PropTypes.string,
        popover: PropTypes.object,
        valuePopover: PropTypes.object,
        step: PropTypes.number,
        min: PropTypes.number,
        max: PropTypes.number,
      })
    ),
    iconGlyph: PropTypes.string,
    mixedLabel: PropTypes.string,
    popover: PropTypes.object,
    valuePopover: PropTypes.object,
    computeFallbackValue: PropTypes.func,
    step: PropTypes.number,
    min: PropTypes.number,
    max: PropTypes.number,
  }).isRequired,
  settingId: PropTypes.string.isRequired,
  onUpdateValue: PropTypes.func.isRequired,
};

export default SliderOption;
